import type { ComponentProps } from "react";

import type { IButtonProps } from "@similarweb/ui-components";
import { Button, Modal } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";

import { StyledActionModalContent, StyledActionModalFooter } from "./styles";

const noop = () => null;

type Props = Omit<ComponentProps<typeof Modal>, "onRequestClose"> & {
    title: string;
    titleHint?: string;
    width?: string;
    isShowCloseBtn?: boolean;
    confirmBtnText?: string;
    cancelBtnText?: string;
    cancelBtnType?: IButtonProps["type"];
    confirmBtnType?: IButtonProps["type"];
    onClose?: VoidFunction;
    onCancel?: VoidFunction;
    onConfirm?: VoidFunction;
};

export const ActionModal = ({
    title,
    titleHint,
    width,
    isShowCloseBtn,
    confirmBtnText,
    cancelBtnText,
    cancelBtnType = "flat",
    confirmBtnType = "primary",
    onClose,
    onCancel,
    onConfirm,
    children,
    ...modalProps
}: Props) => {
    return (
        <Modal
            data-automation="si-action-modal"
            {...modalProps}
            onRequestClose={onClose || onCancel}
            customStyles={{
                ...(modalProps?.customStyles || {}),
                content: {
                    width: width || "400px",
                    maxWidth: width || "400px",
                    ...(modalProps?.customStyles?.content || {}),
                },
            }}
        >
            <Modal.Header data-automation="si-action-modal.header">
                <Modal.Title tooltip={titleHint}>{title}</Modal.Title>
            </Modal.Header>
            {isShowCloseBtn ? (
                <Modal.CloseButton
                    data-automation="si-action-modal.close-btn"
                    onClick={onClose || onCancel || noop}
                />
            ) : null}
            <StyledActionModalContent
                data-automation="si-action-modal.content"
                gap="16px"
            >
                {children}
            </StyledActionModalContent>
            <StyledActionModalFooter data-automation="si-action-modal.footer">
                {onCancel ? (
                    <Button
                        data-automation="si-action-modal.cancel-btn"
                        type={cancelBtnType}
                        onClick={onCancel}
                    >
                        {cancelBtnText || translate("si.common.button.cancel")}
                    </Button>
                ) : null}
                {onConfirm ? (
                    <Button
                        data-automation="si-action-modal.confirm-btn"
                        type={confirmBtnType}
                        onClick={onConfirm}
                    >
                        {confirmBtnText || translate("si.common.button.confirm")}
                    </Button>
                ) : null}
            </StyledActionModalFooter>
        </Modal>
    );
};
